import React, { Dispatch, useState, useEffect, useMemo, memo, useRef } from 'react';

import { useDrag, DragSourceMonitor, useDrop } from 'react-dnd';
import LaunchIcon from '@mui/icons-material/Launch';
import { TransitionProps } from '@mui/material/transitions';
import { animated, useSpring } from 'react-spring';
import Collapse from '@mui/material/Collapse';
import { createStyles, Theme } from '@mui/material/styles';
import { makeStyles, withStyles } from '@mui/styles';
import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem';
import { ItemTypes } from '../../types/ItemTypes';

import style from './DnDTree.module.scss';
import { ReactComponent as Eye } from '../../assets/images/eye-off.svg';
import { UserItem } from './dndUserItem';
import photo from '../../assets/images/Avatar.png';
import { Staff, Subdivision } from '../../types/subdivision';
import { RenderTreeItem } from './Item';
import { useSubdivivsion } from '../../hooks/entityHooks';
import { ReactComponent as EyeOpen } from '../../assets/images/eye.svg';

import { Organization } from '../../types/organization';

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    from: { opacity: 0, transform: 'translate3d(20px,0,0)' },
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}

const StyledTreeItem = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    iconContainer: {
      marginBottom: 26,
      '& .close': {
        opacity: 0.3,
      },
    },
    group: {
      marginLeft: 7,

      paddingLeft: 18,
    },
    label: {
      marginBottom: 26,
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 16,
      // color: '#444A52',

      color: '#FF7221',
    },
  }),
)((props: TreeItemProps) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} />
));

const backUseStyle = makeStyles(
  createStyles({
    root: {
      backgroundColor: 'rgba(235, 86, 79, 0.2)',
    },
  }),
);
type handleProps = {
  id: number;
  type: string;
  handleGetTree?(): void;
};
type ChangeOptions = {
  a: string;
  b: string;
  newParent: number;
  id: number;
  branch: { from: number[]; to: number[] };
  tree?: Subdivision;
  fromOrg: Organization;
  toOrg: Organization;
  isStuff: boolean;
};

type logsInfo = {
  id?: number;
  type?: number;
  handleGetTree?(): void;
};
type UpdateEntity = {
  id: number;
  getTree(): void;
  type: number;
};
type Props = {
  item: Subdivision;
  branch: any;
  org: Organization;
  handleOrganizationInfo({ id, type }: handleProps): void;
  setEmployeeInfoShow: Dispatch<boolean>;
  setOrgInfoShow: Dispatch<boolean>;
  handleSetStaff({ id, type }: logsInfo): void;
  previousName: string;
  handleChangeOptions({ a, b }: ChangeOptions): void;
  handleChangeIsKarmaUser(
    id: number,
    type: number,
    value: boolean,
    handleGetTree: () => void,
  ): void;
  isSearch: boolean;
  expandArray: string[];
  handleSetEntityLoader(val: boolean): void;
  entityLoader: boolean;
  handleGetTree(): void;
  handleSetEntityUpdateArray({ id, getTree, type }: UpdateEntity): void;
};

export const RenderFulledItem = memo(({
  item,
  branch,
  org,
  handleOrganizationInfo,
  setEmployeeInfoShow,
  setOrgInfoShow,
  handleSetStaff,
  previousName,
  handleChangeOptions,
  handleChangeIsKarmaUser,
  isSearch,
  expandArray,
  handleSetEntityLoader,
  entityLoader,
  handleGetTree,
  handleSetEntityUpdateArray,
}: Props) => {
  const [click, setClick] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { getTree, entity: tree } = useSubdivivsion();
  const [start, setStart] = useState(false);

  const [{ opacity }, drag, preview] = useDrag({
    item: {
      name: item.subDivisionName,
      branch,
      type: ItemTypes.SUBDIVISION,
      id: item.id,
      tree,
      org,
    },
    end: (
      item: { name: string; id: number } | undefined,
      monitor: DragSourceMonitor,
    ) => {
      const dropResult = monitor.getDropResult();

      if (item && dropResult) {
        handleChangeOptions({
          a: previousName,
          b: dropResult.name,
          newParent: dropResult.id,
          id: item.id,
          tree: dropResult.tree,
          branch: { from: branch, to: dropResult.branch },
          fromOrg: org,
          toOrg: dropResult.org,
          isStuff: false,
        });
      }
    },
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
  });

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: ItemTypes.SUBDIVISION,
    drop: () => ({
      name: `${item.subDivisionName}`,
      id: `${item.id}`,
      branch,
      tree,
      org,
    }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  const backStyle = backUseStyle();

  const isActive = canDrop && isOver;

  useEffect(() => {
    handleSetEntityUpdateArray({
      id: item.id,
      getTree: handleGetTree,
      type: 2,
    });
  }, [item.id, handleGetTree, handleSetEntityUpdateArray]);

  // Используем useMemo для создания Set из expandArray для O(1) поиска
  const expandSet = React.useMemo(() => new Set(expandArray), [expandArray]);
  
  useEffect(() => {
    if (
      isSearch &&
      !loading &&
      expandSet.has(item.id.toString())
    ) {
      setLoading(true);
      handleSetEntityLoader(true);
      // Загружаем первую страницу с лимитом 40
      getTree(item.id, 1, 40)
        .then(() => {
          setStart(true);
          setCurrentPage(1);
          handleSetEntityLoader(false);
        })
        .catch(() => handleSetEntityLoader(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearch, loading, expandSet, item.id]);

  const getNewSubdivision = async (page: number = 1, limit: number = 40) => {
    await getTree(item.id, page, limit);
  };

  const subdivisionsBranch = useMemo(() => {
    if (tree?.subdivisions) {
      return [...branch, ...tree.subdivisions.map((subdivision) => subdivision.id)];
    }
    return branch;
  }, [tree?.subdivisions, branch]);

  const handleSubdivisionClick = React.useCallback(() => {
    if (!click && !item.subdivisions) {
      // Используем requestAnimationFrame для неблокирующего выполнения
      // Загружаем первую страницу с лимитом 40 для быстрой загрузки
      requestAnimationFrame(() => {
        handleSetEntityLoader(true);
        const response = getTree(item.id, 1, 40);
        response.then(() => {
          handleSetEntityLoader(false);
          setStart(true);
          setCurrentPage(1);
        }).catch(() => {
          handleSetEntityLoader(false);
        });
        setClick(true);
      });
    }
  }, [click, item.subdivisions, item.id, getTree, handleSetEntityLoader]);

  // Подгрузка следующих страниц при скролле
  const loadMore = React.useCallback(async () => {
    if (isLoadingMore || !tree || !tree.subdivisions) return;
    
    // Проверяем, есть ли еще страницы для загрузки
    const totalPage = (tree as any).totalPage;
    if (totalPage && currentPage >= totalPage) {
      return; // Все страницы уже загружены
    }

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    
    try {
      await getTree(item.id, nextPage, 40);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, tree, currentPage, item.id, getTree]);

  // Intersection Observer для отслеживания элемента на позиции "длина - 10"
  useEffect(() => {
    if (!start || !tree || !tree.subdivisions || tree.subdivisions.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [start, tree, isLoadingMore, loadMore]);

  return (
    <div className={style.row} ref={drag}>
      <LaunchIcon
        fontSize="small"
        onClick={() => {
          handleOrganizationInfo({
            type: 'Подразделение',
            id: item.id,
            handleGetTree,
          });
          handleSetStaff({
            id: item.id,
            type: 2,
          });
          setOrgInfoShow(true);
          setEmployeeInfoShow(false);
        }}
        color="secondary"
        className={style.row__pointer}
      />
      <StyledTreeItem
        onClick={handleSubdivisionClick}
        nodeId={item.id.toString()}
        className={isActive ? backStyle.root : ''}
        label={
          <div className={style.label2} ref={drop} style={{ paddingRight: 24 }}>
            <div className={style.label}>
              <div>{item.subDivisionName}</div>
              &nbsp;
              <div style={{ color: '#CACED2' }}>
                {item.staffsCount ? ` (${item.staffsCount})` : '(0)'}
              </div>
            </div>
          </div>
        }
      >
        {tree && start && tree.subdivisions && tree.subdivisions.length ? (
          <>
            <RenderTreeItem
              isSearch={isSearch}
              setEmployeeInfoShow={setEmployeeInfoShow}
              setOrgInfoShow={setOrgInfoShow}
              tree={tree.subdivisions}
              branch={subdivisionsBranch}
              org={org}
              handleSetStaff={handleSetStaff}
              handleOrganizationInfo={handleOrganizationInfo}
              previousName={tree.subDivisionName}
              handleChangeOptions={handleChangeOptions}
              expandArray={expandArray}
              handleSetEntityLoader={handleSetEntityLoader}
              entityLoader={entityLoader}
              handleChangeIsKarmaUser={handleChangeIsKarmaUser}
              handleGetTree={getNewSubdivision}
              handleSetEntityUpdateArray={handleSetEntityUpdateArray}
              loadMoreRef={loadMoreRef}
            />
            {isLoadingMore && (
              <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                Загрузка...
              </div>
            )}
          </>
        ) : null}

        {tree && start && tree.staff.length
          ? tree.staff.map((employee) => (
              <UserItem
                key={employee.id}
                item={tree}
                photo={photo}
                Eye={Eye}
                setEmployeeInfoShow={setEmployeeInfoShow}
                setOrgInfoShow={setOrgInfoShow}
                {...employee}
                handleSetStaff={handleSetStaff}
                previousName={tree.subDivisionName}
                handleChangeOptions={handleChangeOptions}
                handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                handleGetTree={getNewSubdivision}
                handleSetEntityUpdateArray={handleSetEntityUpdateArray}
              />
            ))
          : null}
      </StyledTreeItem>
      <span
        style={{ width: 24, position: 'absolute', right: 0, cursor: 'pointer' }}
      >
        {item.isKarmaUser ? (
          <EyeOpen
            onClick={async () => {
              if (handleGetTree) {
                await handleChangeIsKarmaUser(
                  item.id,
                  2,
                  !item.isKarmaUser,
                  handleGetTree,
                );
                await getTree(item.id);
              }
            }}
          />
        ) : (
          <Eye
            stroke="#CACED2"
            onClick={async () => {
              if (handleGetTree) {
                await handleChangeIsKarmaUser(
                  item.id,
                  2,
                  !item.isKarmaUser,
                  handleGetTree,
                );
                await getTree(item.id);
              }
            }}
          />
        )}
        {/*
        {tree ? (
          tree.karmaUser ?? tree.isKarmaUser ? (
            <EyeOpen
              onClick={async () => {
                if (handleGetTree) {
                  await handleChangeIsKarmaUser(
                    tree.id,
                    2,
                    !tree.karmaUser ?? !tree.isKarmaUser,
                    handleGetTree,
                  );
                  await getTree(tree.id);
                }
              }}
            />
          ) : (
            <Eye
              stroke="#CACED2"
              onClick={async () => {
                if (handleGetTree) {
                  await handleChangeIsKarmaUser(
                    tree.id,
                    2,
                    !tree.karmaUser ?? !tree.isKarmaUser,
                    handleGetTree,
                  );
                  await getTree(tree.id);
                }
              }}
            />
          )
        ) : item.isKarmaUser ? (
          <EyeOpen
            onClick={async () => {
              if (handleGetTree) {
                await handleChangeIsKarmaUser(
                  item.id,
                  2,
                  !item.isKarmaUser,
                  handleGetTree,
                );
                await getTree(item.id);
              }
            }}
          />
        ) : (
          <Eye
            stroke="#CACED2"
            onClick={async () => {
              if (handleGetTree) {
                await handleChangeIsKarmaUser(
                  item.id,
                  2,
                  !item.isKarmaUser,
                  handleGetTree,
                );
                await getTree(item.id);
              }
            }}
          />
        )}
*/}
      </span>
    </div>
  );
});
